import type { FsId, Hermite, RawEventOfKind, TimeStampMs } from '@fullstory/recording-api';
import { PerfEntryType, RawEventKind, RecAnimationType } from '@fullstory/recording-api';
import type { LayoutViewportSize } from '@fullstory/shared/viewport';
import { assertExhaustive } from '@fullstory/typeutils';
import { DefaultMap } from '@fullstory/util/defaultmap';
import type { Point, Vec2D } from '@fullstory/util/geometry';
import { FSDialogState, getDialogState } from '../fsdom/fsdialog';
import { classifyResource } from '../resources/classify';
import { AnimationEventTimeline } from './animation_event_timeline';
import type { DocumentEvents } from './document_events';
import { HermiteCurve2d } from './hermite_curves';
import { KeyframeEffectEventTimeline } from './keyframe_effect_event_timeline';
import type { ImmutableCumulativeTimeline } from './timeline/cumulative_timeline';
import { CumulativeTimeline } from './timeline/cumulative_timeline';
import type { ImmutableTimeline } from './timeline/timeline';
import { Timeline } from './timeline/timeline';

type AddSubdocumentArgs =
  | [RawEventOfKind<'SET_FRAME_BASE'>, FsId, DocumentEvents]
  | [RawEventOfKind<'UNLOAD'>, FsId, undefined];

/**
 * Strategies for rendering color scheme media queries
 */
type ColorSchemeKind =
  | 'no-preference' // Behave as if user has no known theme
  | 'light' // Behave as if user's theme is light mode
  | 'dark'; // Behave as if user's theme is dark mode

/**
 * Strategies for rendering pointer media queries
 */
type PointerKind =
  | 'fine' // Behave as if user's primary pointer is fine (e.g. using a mouse)
  | 'coarse' // Behave as if user's primary pointer is coarse (e.g. using a touch device)
  | 'none'; // Behave as if user has no primary pointer

/** A resource that was fetched at recording time. */
type ResourceFetch = Pick<PerformanceResourceTiming, 'fetchStart' | 'responseEnd'> & {
  destination: RequestDestination;
  href: string;
};

/**
 * Description of current cursor selection
 */
type Selection = {
  startId?: FsId;
  startOffset?: number;
  endId?: FsId;
  endOffset?: number;
};

/**
 * A timeline for events with document scope - in other words, events which are
 * "local" to a certain document and don't have effects outside of that
 * document. This is in contrast to page scope events, which affect all
 * documents on a page; {@see DocumentEventTimeline} for more discussion on the
 * distinction.
 */
export class DocumentEventTimeline {
  /**
   * @return the timeline for the subdocument associated with each iframe in the
   * document. The value is undefined when we don't have any information about
   * the subdocument in that iframe; this is an expected situation for
   * cross-origin iframes that we couldn't record.
   */
  public get subdocument(): DefaultMap<FsId, ImmutableTimeline<DocumentEvents>> {
    return this._subdocument;
  }
  private readonly _subdocument = new DefaultMap<FsId, Timeline<DocumentEvents>>(
    () => new Timeline<DocumentEvents>(),
  );

  /**
   * @return the timeline for this document's resources. The value at each point
   * is the set of resources that had *started* loading at that point. Note that
   * resources that are irrelevant to playback, such as scripts, are not
   * included.
   */
  public get resourceFetches(): ImmutableCumulativeTimeline<ResourceFetch> {
    return this._resourceFetches;
  }
  private readonly _resourceFetches = new CumulativeTimeline<ResourceFetch>();

  /**
   * @return the timeline for the document's focus state. A document has focus
   * if it's the foreground document that the user is viewing or interacting
   * with, and loses focus if it moves into the background. The value is
   * undefined when we don't know the focus state.
   */
  public get documentFocused(): ImmutableTimeline<boolean> {
    return this._documentFocused;
  }
  private readonly _documentFocused = new Timeline<boolean>();

  /**
   * @return the timelines for the layout viewport position for each scrollable
   * box in the document. The values are undefined when we don't know the position.
   */
  public get layoutViewportPos(): DefaultMap<FsId, ImmutableTimeline<Point>> {
    return this._layoutViewportPos;
  }
  private readonly _layoutViewportPos = new DefaultMap<FsId, Timeline<Point>>(
    () => new Timeline<Point>(),
  );

  public get keyframeEffects(): DefaultMap<number, KeyframeEffectEventTimeline> {
    return this._keyframeEffects;
  }
  private readonly _keyframeEffects = new DefaultMap<number, KeyframeEffectEventTimeline>(
    () => new KeyframeEffectEventTimeline(),
  );

  public get animations(): DefaultMap<number, AnimationEventTimeline> {
    return this._animations;
  }
  private readonly _animations = new DefaultMap<number, AnimationEventTimeline>(
    () => new AnimationEventTimeline(),
  );

  public get dialogs(): DefaultMap<number, Timeline<FSDialogState>> {
    return this._dialogs;
  }

  private readonly _dialogs = new DefaultMap<FsId, Timeline<FSDialogState>>(
    () => new Timeline<FSDialogState>(),
  );

  /**
   * @return the timeline for the document's layout viewport size. The value is
   * undefined when we don't know the size. Note that we don't collect this
   * information for all scrollable boxes, since their size can be established
   * by other means. This is only for top-level document scrolling.
   */
  public get layoutViewportSize(): ImmutableTimeline<LayoutViewportSize> {
    return this._layoutViewportSize;
  }
  private readonly _layoutViewportSize = new Timeline<LayoutViewportSize>();

  public get selection(): ImmutableTimeline<Selection> {
    return this._selection;
  }
  private readonly _selection = new Timeline<Selection>();

  /**
   * @return the timelines for the offset between each scrollable box's layout
   * viewport position and the position of its visual viewport. The value is
   * undefined when we don't know the offset. Note that as things stand today,
   * no browser supports a visual viewport for non-top-level scrollable boxes,
   * so this is de facto for top-level document scrolling only.
   */
  public get visualViewportOffset(): DefaultMap<FsId, ImmutableTimeline<Vec2D>> {
    return this._visualViewportOffset;
  }
  private readonly _visualViewportOffset = new DefaultMap<FsId, Timeline<Vec2D>>(
    () => new Timeline<Vec2D>(),
  );

  /**
   * @return the timeline for the document's visual viewport size. The value is
   * undefined when we don't know the size. Since no browser supports a visual
   * viewport except for top-level document scrolling, we don't collect this
   * information for nested scrollable boxes.
   */
  public get visualViewportSize(): ImmutableTimeline<Vec2D> {
    return this._visualViewportSize;
  }
  private readonly _visualViewportSize = new Timeline<Vec2D>();

  /** @return the timeline for the page's active {@link PointerKind} */
  public get pointerKind(): ImmutableTimeline<PointerKind> {
    return this._pointerKind;
  }
  private readonly _pointerKind = new Timeline<PointerKind>();

  /** @return the timeline for the page's active {@link ColorSchemeKind} */
  public get colorSchemeKind(): ImmutableTimeline<ColorSchemeKind> {
    return this._colorSchemeKind;
  }
  private readonly _colorSchemeKind = new Timeline<ColorSchemeKind>();

  addSubdocument(...[event, frameId, docEvents]: AddSubdocumentArgs): void {
    this._subdocument.get(frameId).addEventWithValue(event, docEvents);
  }

  addPerformanceEntryEvent(event: RawEventOfKind<'PERF_ENTRY'>, baseUrl: string): void {
    if (event.Args[0] !== PerfEntryType.Resource) {
      return;
    }

    const href = event.Args[1] as string;
    let url: URL;
    try {
      url = new URL(href, baseUrl);
    } catch (e) {
      console.warn(`Couldn't parse URL '${href}' from performance event: ${e}`);
      return;
    }

    const classification = classifyResource(url, {
      initiatorType: event.Args[4] as string,
    });
    if (classification === 'blocklisted') {
      return; // Pretend blocklisted resources don't exist.
    }
    if (classification === 'unknown') {
      return; // This resource isn't interesting for playback.
    }

    const fetchStart = event.Args[7] as number;
    this._resourceFetches.add(
      {
        destination: classification,
        href,
        fetchStart,
        responseEnd: event.Args[15] as number,
      },
      fetchStart,
    );
  }

  addDocumentFocusEvent(event: RawEventOfKind<'WINDOW_FOCUS' | 'WINDOW_BLUR'>): void {
    switch (event.Kind) {
      case RawEventKind.WINDOW_FOCUS:
        this._documentFocused.addEventWithValue(event, true);
        break;
      case RawEventKind.WINDOW_BLUR:
        this._documentFocused.addEventWithValue(event, false);
        break;
      default:
        assertExhaustive(event, `Unexpected document focus event: ${JSON.stringify(event)}`);
    }
  }

  addAnimationEvent(event: RawEventOfKind<'ANIMATION_CREATED' | 'ANIMATION_METHOD_CALLED'>) {
    switch (event.Kind) {
      case RawEventKind.ANIMATION_CREATED: {
        const id = event.Args[0];
        const type = event.Args[1];

        // TODO (jordanhurt) - support CSSAnimation and CSSTransition at some point
        if (type === RecAnimationType.Animation) {
          this._animations.get(id).addCreatedEvent(event);
        }

        break;
      }
      case RawEventKind.ANIMATION_METHOD_CALLED: {
        const id = event.Args[0];
        this._animations.get(id).addMethodEvent(event);
        break;
      }
    }
  }

  addKeyframeEffectEvent(
    event: RawEventOfKind<
      'KEYFRAME_EFFECT_CREATED' | 'KEYFRAME_EFFECT_METHOD_CALLED' | 'KEYFRAME_EFFECT_PROPERTY_SET'
    >,
  ) {
    const id = event.Args[0];
    const eventTimeline = this._keyframeEffects.get(id);

    switch (event.Kind) {
      case RawEventKind.KEYFRAME_EFFECT_CREATED: {
        eventTimeline.addCreatedEvent(event);
        break;
      }
      case RawEventKind.KEYFRAME_EFFECT_METHOD_CALLED: {
        eventTimeline.addMethodCalledEvent(event);
        break;
      }
      case RawEventKind.KEYFRAME_EFFECT_PROPERTY_SET: {
        eventTimeline.addPropertySetEvent(event);
        break;
      }
    }
  }

  addDialogEvent(event: RawEventOfKind<'DIALOG' | 'MUT_ATTR'>) {
    const id = event.Args[0];
    const dialog = this._dialogs.get(id);
    const state = getDialogState(event);
    if (dialog && state) {
      dialog.addEventWithValue(event, state);
    }
  }

  addLayoutScrollEvent(event: RawEventOfKind<'SCROLL_LAYOUT' | 'SCROLL_LAYOUT_CURVE'>): void {
    switch (event.Kind) {
      case RawEventKind.SCROLL_LAYOUT: {
        const point = { x: event.Args[1], y: event.Args[2] };
        this._layoutViewportPos.get(event.Args[0]).addEventWithValue(event, point);
        break;
      }

      case RawEventKind.SCROLL_LAYOUT_CURVE: {
        const xCurve: Hermite = [event.Args[2], event.Args[3], event.Args[4], event.Args[5]];
        const yCurve: Hermite = [event.Args[6], event.Args[7], event.Args[8], event.Args[9]];
        const curveFunction = new HermiteCurve2d(xCurve, yCurve);
        const curveEnd: TimeStampMs = event.Args[0];
        this._layoutViewportPos
          .get(event.Args[1])
          .addEventWithCurve(event, curveEnd, curveFunction);
        break;
      }

      default:
        assertExhaustive(event, `Unexpected layout scroll event: ${JSON.stringify(event)}`);
    }
  }

  addLayoutResizeEvent(event: RawEventOfKind<'RESIZE_LAYOUT'>): void {
    const size: LayoutViewportSize = {
      clientWidth: event.Args[0],
      clientHeight: event.Args[1],
      width: event.Args[2] ?? event.Args[0],
      height: event.Args[3] ?? event.Args[1],
    };
    this._layoutViewportSize.addEventWithValue(event, size);
  }

  addSelectionEvent(event: RawEventOfKind<'SELECT'>): void {
    const selection = {
      startId: event.Args[0],
      startOffset: event.Args[1],
      endId: event.Args[2],
      endOffset: event.Args[3],
    };
    this._selection.addEventWithValue(event, selection);
  }

  addVisualOffsetScrollEvent(
    event: RawEventOfKind<'SCROLL_VISUAL_OFFSET' | 'SCROLL_VISUAL_OFFSET_CURVE'>,
  ): void {
    switch (event.Kind) {
      case RawEventKind.SCROLL_VISUAL_OFFSET: {
        const point = { x: event.Args[1], y: event.Args[2] };
        this._visualViewportOffset.get(event.Args[0]).addEventWithValue(event, point);
        break;
      }

      case RawEventKind.SCROLL_VISUAL_OFFSET_CURVE: {
        const xCurve: Hermite = [event.Args[2], event.Args[3], event.Args[4], event.Args[5]];
        const yCurve: Hermite = [event.Args[6], event.Args[7], event.Args[8], event.Args[9]];
        const curveFunction = new HermiteCurve2d(xCurve, yCurve);
        const curveEnd: TimeStampMs = event.Args[0];
        this._visualViewportOffset
          .get(event.Args[1])
          .addEventWithCurve(event, curveEnd, curveFunction);
        break;
      }

      default:
        assertExhaustive(event, `Unexpected visual offset scroll event: ${JSON.stringify(event)}`);
    }
  }

  addVisualResizeEvent(event: RawEventOfKind<'RESIZE_VISUAL' | 'RESIZE_VISUAL_CURVE'>): void {
    switch (event.Kind) {
      case RawEventKind.RESIZE_VISUAL: {
        const size = { x: event.Args[0], y: event.Args[1] };
        this._visualViewportSize.addEventWithValue(event, size);
        break;
      }

      case RawEventKind.RESIZE_VISUAL_CURVE: {
        const xCurve: Hermite = [event.Args[1], event.Args[2], event.Args[3], event.Args[4]];
        const yCurve: Hermite = [event.Args[5], event.Args[6], event.Args[7], event.Args[8]];
        const curveFunction = new HermiteCurve2d(xCurve, yCurve);
        const curveEnd: TimeStampMs = event.Args[0];
        this._visualViewportSize.addEventWithCurve(event, curveEnd, curveFunction);
        break;
      }

      default:
        assertExhaustive(event, `Unexpected visual resize event: ${JSON.stringify(event)}`);
    }
  }

  addMediaQueryChangeEvent(event: RawEventOfKind<'MEDIA_QUERY_CHANGE'>): void {
    switch (event.Args[0]) {
      case 'pointer': {
        let newMode: PointerKind = 'fine';
        switch (event.Args[1]) {
          case 'fine':
          case 'coarse':
          case 'none':
            newMode = event.Args[1];
            break;
        }
        this._pointerKind.addEventWithValue(event, newMode);
        break;
      }
      case 'prefers-color-scheme': {
        let newMode: ColorSchemeKind = 'no-preference';
        switch (event.Args[1]) {
          case 'no-preference':
          case 'light':
          case 'dark':
            newMode = event.Args[1];
            break;
        }
        this._colorSchemeKind.addEventWithValue(event, newMode);
        break;
      }

      default:
      // NOTE (eugenechang) any-pointer and any-hover are page level events
    }
  }
}
